import React, { useEffect, useState, useRef } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';
import { toast } from 'react-toastify';

const msalInstance = new PublicClientApplication(msalConfig);

export const AzureSSOProvider = ({ children }) => (
  <MsalProvider instance={msalInstance}>{children}</MsalProvider>
);
export const useAzureSSO = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  // localStorage.setItem("accessToken", account.idToken);
  const [accessToken, setAccessToken] = useState(null);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const toastShownRef = useRef(false);

  const LOGIN_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
  // const LOGIN_TIMEOUT = 10 * 1000; // 10 seconds in milliseconds

  /**
   * Store login timestamp in localStorage
   */
  const storeLoginTime = () => {
    localStorage.setItem("loginTime", Date.now().toString());
    toastShownRef.current = false;
  };

  /**
   * Checks if the session has expired (3-hour limit)
   */
  const checkSessionExpiration = () => {
    const loginTime = localStorage.getItem("loginTime");
    const sessionExpired = localStorage.getItem("sessionExpired");
    if (loginTime && !sessionExpired) {
      const timeElapsed = Date.now() - parseInt(loginTime, 10);
      if (timeElapsed >= LOGIN_TIMEOUT && !toastShownRef.current) {
        toastShownRef.current = true;
        // alert("Session expired. Please login again.");
        setTimeout(() => {
          logout(); // Auto logout after showing toast
        }, 1000);
      }
    }
  };

  /**
   * Store token in local storage
   */
  const storeAccessToken = (token) => {
    localStorage.setItem("accessToken", token);
  };

  /**
   * Acquire a new access token silently
   */
  const acquireTokenSilent = async () => {
    if (!account) return;
    // debugger;
    const request = {
      ...loginRequest,
      account: account,
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      const newAccessToken = response.accessToken;

      // Store the new access token in localStorage
      setAccessToken(newAccessToken);
      storeAccessToken(newAccessToken);
      setIsTokenReady(true)

      return newAccessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.error("Silent token acquisition failed, redirecting to login:", error);
        await instance.acquireTokenRedirect(request);
      } else {
        console.error("Failed to acquire token silently:", error);
        toast.error("Failed to refresh token. Please login again.");
      }
    }
  };


  /**
   * Handle the authentication process after redirect
   */
  useEffect(() => {
    if (inProgress === InteractionStatus.HandleRedirect) {
      instance.handleRedirectPromise()
        .then(() => acquireTokenSilent())
        .catch((error) => {
          console.error("Error handling redirect:", error);
          toast.error("Authentication failed, please try again.");
        });
    }
  }, [instance, inProgress]);

  /**
   * Automatically refresh the token before it expires
   */
  useEffect(() => {
    if (isAuthenticated && account) {
      if (!localStorage.getItem("loginTime")) {
        storeLoginTime();
      } else {
        checkSessionExpiration();
      }
      acquireTokenSilent();

      // Set up an interval to refresh the token periodically
      const intervalId = setInterval(() => {
        acquireTokenSilent();
      }, 5 * 60 * 1000); // Refresh every 5 minutes

      return () => clearInterval(intervalId);
    }
  }, [isAuthenticated, account]);

  /**
   * Handles the login flow
   */
  const login = async () => {
    try {
      if (inProgress === InteractionStatus.None) {
        await instance.loginRedirect(loginRequest);
        storeLoginTime();
      }
    } catch (error) {
      console.error("Login failed", error);
      toast.error("Failed to login. Please try again.");
    }
  };


  /**
   * Handles the logout flow
   */
  const logout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await instance.clearCache();
      window.location.href = `https://login.microsoftonline.com/common/oauth2/logout?post_logout_redirect_uri=${encodeURIComponent(
        window.location.origin
      )}`;
    } catch (error) {
      console.error("Logout failed", error);
      toast.error("Failed to logout. Please try again.");
    }
  };

  return { isAuthenticated, account, accessToken, login, logout, acquireTokenSilent, isTokenReady };
};